import { Button, Modal } from 'antd';
import styled from 'styled-components';

import { media } from '../../../../styles/breakpoints';

export const ModalStyled = styled(Modal)`
  color: var(--gray-200);

  &.ant-modal {
    max-width: calc(100vw - 32px);

    ${media.md} {
      width: calc(100vw - 32px) !important;
      margin: 16px auto;
    }
  }

  .ant-modal-content {
    border-radius: 12px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }

  .ant-modal-header {
    border-radius: 12px 12px 0 0;
    padding: 20px 24px 16px;
    border-bottom: none;
    background: #fafafa;

    ${media.md} {
      padding: 16px 16px 12px;
    }
  }

  .ant-modal-title {
    font-size: 18px !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.85) !important;
    line-height: 1.4 !important;

    ${media.md} {
      font-size: 16px !important;
    }
  }

  .ant-modal-body {
    padding: 20px 24px 24px;
    font-size: 15px;

    ${media.md} {
      padding: 16px;
      font-size: 14px;
    }
  }

  .ant-modal-close {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.08);
    }

    .ant-modal-close-x {
      font-size: 16px;
      width: 36px;
      height: 36px;
      line-height: 36px;
      color: rgba(0, 0, 0, 0.45);
    }
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  ${media.md} {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

type InfoItemProps = {
  $color?: string;
};

export const InfoItem = styled.div<InfoItemProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 10px;
  border-left: 3px solid ${({ $color }) => $color || '#d9d9d9'};
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  &.full-width {
    grid-column: span 2;
    border-left: none;
    background: transparent;
    padding: 0;
    margin-top: 8px;

    &:hover {
      background: transparent;
    }

    ${media.md} {
      grid-column: span 1;
    }
  }

  ${media.md} {
    padding: 12px 14px;
    gap: 10px;
  }
`;

export const InfoIcon = styled.div<InfoItemProps>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $color }) =>
    $color ? `color-mix(in srgb, ${$color} 12%, transparent)` : '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ $color }) => $color || '#8c8c8c'};
  }

  ${media.md} {
    width: 32px;
    height: 32px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const InfoContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 2px;
`;

export const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.3;

  ${media.md} {
    font-size: 14px;
  }
`;

export const DownloadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const DownloadButton = styled(Button).attrs({
  type: 'primary',
  block: true,
})`
  background: var(--primary) !important;
  border: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px;
  height: 44px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s !important;

  &:hover {
    opacity: 0.9 !important;
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    font-size: 18px;
  }
`;

export const OpenFileButton = styled(Button).attrs({
  type: 'default',
  block: true,
})`
  background: #f6ffed !important;
  border: 1px solid #b7eb8f !important;
  color: #52c41a !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px;
  font-weight: 500 !important;
  font-size: 15px !important;
  height: 44px !important;
  border-radius: 8px !important;
  transition: all 0.2s !important;

  &:hover,
  &:focus {
    background: #52c41a !important;
    border-color: #52c41a !important;
    color: white !important;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: #f5f5f5 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed !important;
  }

  svg {
    font-size: 18px;
  }
`;

export const AlreadyDownloadedText = styled.div`
  font-size: 13px;
  color: #0369a1;
  font-weight: 500;
  padding: 10px 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  text-align: center;
  line-height: 1.4;
`;

export const ErrorInstructions = styled.div`
  padding: 14px 16px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
`;

export const ErrorTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ErrorMessage = styled.div`
  font-size: 13px;
  color: #a16207;
  line-height: 1.5;
`;

export const WhatsappContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 14px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;

  ${media.md} {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
`;

export const WhatsappText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #166534;
`;

export const WhatsappLink = styled.a.attrs({
  target: '_blank',
})`
  background: #25d366;
  color: white !important;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #20c057;
    color: white !important;
  }

  ${media.md} {
    width: 100%;
    justify-content: center;
  }
`;

export const WhatsappImage = styled.img.attrs({
  src: './images/whatsapp.png',
  alt: 'Whatsapp icon',
})`
  width: 20px;
  height: 20px;
`;
